// ui/questModal.js
// Quest journal modal - view and manage quests

import { gameState } from '../game/gameState.js';
import { getQuest, getQuestGiver, getQuestsByAct, getTotalQuestCount, QUEST_ACTS, QUEST_GIVERS } from '../data/quests/questData.js';
import { getItem, getItemName, getItemIcon } from '../data/items/index.js';
import { formatNumber, formatTime } from '../utils/format.js';
import { toastSuccess, toastWarning, toastError } from './toast.js';
import { showConfirmModal } from './confirmModal.js';
import { skillRegistry } from '../core/skillRegistry.js';
import { SKILLS } from '../game/constants.js';
import { addXP } from '../systems/experience.js';
import { MILESTONES } from '../skills/architecting/architectingData.js';
import { PETS } from '../skills/husbandry/husbandryData.js';
import logger from '../utils/logger.js';

/**
 * Get display name for a milestone
 */
function getMilestoneDisplayName(milestoneId) {
    const milestone = Object.values(MILESTONES).find(m => m.id === milestoneId);
    return milestone?.name || milestoneId;
}

/**
 * Get icon for a milestone
 */
function getMilestoneIcon(milestoneId) {
    const milestone = Object.values(MILESTONES).find(m => m.id === milestoneId);
    return milestone?.icon || '🏗️';
}

/**
 * Get display name for a pet
 */
function getPetDisplayName(petId) {
    const pet = Object.values(PETS).find(p => p.id === petId);
    return pet?.name || petId;
}

/**
 * Get icon for a pet
 */
function getPetIcon(petId) {
    const pet = Object.values(PETS).find(p => p.id === petId);
    return pet?.icon || '🐾';
}

let modalElement = null;
let selectedQuestNumber = null;
let updateIntervalId = null;
let searchQuery = '';

/**
 * Show the quest modal
 */
export function showQuestModal() {
    selectedQuestNumber = null;
    searchQuery = '';

    const activeQuest = gameState.getActiveQuest();
    if (activeQuest) {
        selectedQuestNumber = activeQuest.questNumber;
    }

    renderModal();
}

/**
 * Render the full modal
 */
function renderModal() {
    const completed = gameState.getCompletedQuestCount();
    const total = getTotalQuestCount();

    const html = `
        <div class="modal-overlay quest-modal-overlay" id="quest-modal-overlay">
            <div class="modal-content quest-modal">
                <div class="modal-header">
                    <h2>Quest Journal</h2>
                    <span class="quest-count">${completed}/${total}</span>
                    <button class="modal-close" onclick="window.questModal.close()">&times;</button>
                </div>
                <div class="modal-body quest-modal-body">
                    <div class="quest-sidebar">
                        ${renderSearchBar()}
                        ${renderActiveQuestTracker()}
                        ${renderQuestList()}
                    </div>
                    <div class="quest-details">
                        ${renderQuestDetails()}
                    </div>
                </div>
            </div>
        </div>
    `;

    // Remove existing modal if any
    if (modalElement) {
        modalElement.remove();
    }

    // Create and append modal
    modalElement = document.createElement('div');
    modalElement.innerHTML = html;
    document.body.appendChild(modalElement);

    // Add fade-in animation
    requestAnimationFrame(() => {
        const overlay = document.getElementById('quest-modal-overlay');
        if (overlay) overlay.classList.add('active');
    });

    // Close on overlay click (outside modal)
    const overlay = document.getElementById('quest-modal-overlay');
    if (overlay) {
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeQuestModal();
            }
        });
    }

    // Close on Escape key
    document.addEventListener('keydown', handleKeydown);

    // Start update interval for active quest progress
    startUpdateInterval();
}

function handleKeydown(e) {
    if (e.key === 'Escape') {
        closeQuestModal();
    }
}

/**
 * Close the quest modal
 */
export function closeQuestModal() {
    document.removeEventListener('keydown', handleKeydown);
    stopUpdateInterval();

    const overlay = document.getElementById('quest-modal-overlay');
    if (overlay) {
        overlay.classList.remove('active');
        overlay.classList.add('closing');

        setTimeout(() => {
            if (modalElement) {
                modalElement.remove();
                modalElement = null;
            }
        }, 200);
    }

    selectedQuestNumber = null;
    searchQuery = '';
}

/**
 * Render search bar
 */
function renderSearchBar() {
    return `
        <div class="quest-search">
            <input type="text"
                   id="quest-search-input"
                   class="quest-search-input"
                   placeholder="Search quests or rewards..."
                   value="${searchQuery}"
                   oninput="window.questModal.handleSearch(this.value)">
            ${searchQuery ? `<button class="quest-search-clear" onclick="window.questModal.clearSearch()">×</button>` : ''}
        </div>
    `;
}

/**
 * Handle search input
 */
export function handleSearch(query) {
    searchQuery = query.toLowerCase().trim();
    refreshQuestList();
}

/**
 * Clear search
 */
export function clearSearch() {
    searchQuery = '';
    const input = document.getElementById('quest-search-input');
    if (input) input.value = '';
    refreshQuestList();
}

/**
 * Refresh only the quest list section
 */
function refreshQuestList() {
    const sidebar = document.querySelector('.quest-sidebar');
    if (!sidebar) return;

    sidebar.innerHTML = `
        ${renderSearchBar()}
        ${renderActiveQuestTracker()}
        ${renderQuestList()}
    `;

    // Re-focus the search input if there was a search
    if (searchQuery) {
        const input = document.getElementById('quest-search-input');
        if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }
}

/**
 * Check if a quest matches the search query
 */
function questMatchesSearch(quest) {
    if (!searchQuery) return true;

    // Check quest name
    if (quest.name.toLowerCase().includes(searchQuery)) return true;

    // Check quest giver
    const giver = QUEST_GIVERS[Object.keys(QUEST_GIVERS).find(k => QUEST_GIVERS[k].id === quest.giver)];
    if (giver?.name.toLowerCase().includes(searchQuery)) return true;

    // Check rewards
    if (quest.rewards?.items) {
        for (const itemId of Object.keys(quest.rewards.items)) {
            const item = getItem(itemId);
            if (item?.name.toLowerCase().includes(searchQuery)) return true;
            if (itemId.toLowerCase().includes(searchQuery)) return true;
        }
    }

    // Check unlocks
    if (quest.rewards?.unlocks) {
        for (const unlockId of quest.rewards.unlocks) {
            if (unlockId.toLowerCase().includes(searchQuery)) return true;
            const unlockName = getUnlockDisplayName(unlockId);
            if (unlockName.toLowerCase().includes(searchQuery)) return true;
        }
    }

    return false;
}

/**
 * Start update interval for active quest progress
 */
function startUpdateInterval() {
    stopUpdateInterval();
    updateIntervalId = setInterval(() => {
        updateActiveQuestProgress();
    }, 1000);
}

/**
 * Stop update interval
 */
function stopUpdateInterval() {
    if (updateIntervalId) {
        clearInterval(updateIntervalId);
        updateIntervalId = null;
    }
}

/**
 * Update active quest progress bar and timer
 */
function updateActiveQuestProgress() {
    const activeQuest = gameState.getActiveQuest();
    if (!activeQuest) return;

    const quest = getQuest(activeQuest.questNumber);
    if (!quest) return;

    const progress = gameState.getQuestProgress();
    const isComplete = progress >= 1;
    const elapsed = Math.max(0, Date.now() - activeQuest.startedAt);
    const remaining = Math.max(0, quest.duration - elapsed);

    // Update progress bar
    const progressBar = document.querySelector('.active-quest-progress-fill');
    if (progressBar) {
        progressBar.style.width = `${progress * 100}%`;
    }

    // Update timer
    const timerEl = document.querySelector('.active-quest-timer');
    if (timerEl) {
        if (isComplete) {
            timerEl.textContent = 'Complete!';
            timerEl.classList.add('complete');
        } else {
            timerEl.textContent = formatTime(remaining / 1000);
        }
    }

    // Update complete button visibility
    const completeBtn = document.querySelector('.btn-complete-quest');
    if (completeBtn) {
        completeBtn.disabled = !isComplete;
    }

    // Also update the selected quest details if it's the active one
    if (selectedQuestNumber === activeQuest.questNumber) {
        const detailTimer = document.querySelector('.quest-detail-timer');
        if (detailTimer) {
            if (isComplete) {
                detailTimer.textContent = 'Ready to complete!';
                detailTimer.classList.add('complete');
            } else {
                detailTimer.textContent = `Time remaining: ${formatTime(remaining / 1000)}`;
            }
        }

        const detailProgressBar = document.querySelector('.quest-detail-progress-fill');
        if (detailProgressBar) {
            detailProgressBar.style.width = `${progress * 100}%`;
        }
    }
}

/**
 * Render active quest tracker section
 */
function renderActiveQuestTracker() {
    const activeQuest = gameState.getActiveQuest();

    if (!activeQuest) {
        return `
            <div class="active-quest-tracker empty">
                <span class="tracker-label">No Active Quest</span>
                <span class="tracker-hint">Select a quest below to begin</span>
            </div>
        `;
    }

    const quest = getQuest(activeQuest.questNumber);
    if (!quest) return '';

    const giver = QUEST_GIVERS[Object.keys(QUEST_GIVERS).find(k => QUEST_GIVERS[k].id === quest.giver)];
    const progress = gameState.getQuestProgress();
    const isComplete = progress >= 1;
    const elapsed = Math.max(0, Date.now() - activeQuest.startedAt);
    const remaining = Math.max(0, quest.duration - elapsed);

    return `
        <div class="active-quest-tracker" onclick="window.questModal.selectQuest(${activeQuest.questNumber})">
            <div class="tracker-header">
                <span class="tracker-label">Active Quest</span>
                <span class="active-quest-timer ${isComplete ? 'complete' : ''}">${isComplete ? 'Complete!' : formatTime(remaining / 1000)}</span>
            </div>
            <div class="active-quest-info">
                <span class="active-quest-giver">${giver?.icon || '?'}</span>
                <span class="active-quest-name">${quest.name}</span>
            </div>
            <div class="active-quest-progress">
                <div class="active-quest-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
            <div class="tracker-actions">
                <button class="btn btn-small btn-complete-quest"
                        onclick="event.stopPropagation(); window.questModal.completeQuest()"
                        ${!isComplete ? 'disabled' : ''}>
                    Claim Rewards
                </button>
                ${!isComplete ? `
                    <button class="btn btn-small btn-cancel-quest"
                            onclick="event.stopPropagation(); window.questModal.cancelQuest()">
                        Abandon
                    </button>
                ` : ''}
            </div>
        </div>
    `;
}

/**
 * Render quest list organized by acts
 */
function renderQuestList() {
    let html = '<div class="quest-list">';

    // Track if any quests match search
    let anyMatches = false;

    for (const actKey of Object.keys(QUEST_ACTS)) {
        const act = QUEST_ACTS[actKey];
        const allQuests = getQuestsByAct(parseInt(act.id.split('_')[1]));

        // Filter quests by search
        const quests = allQuests.filter(q => questMatchesSearch(q));

        if (quests.length === 0 && !searchQuery) continue;
        if (quests.length === 0 && searchQuery) continue; // Skip empty acts in search

        anyMatches = anyMatches || quests.length > 0;

        const completedInAct = allQuests.filter(q => gameState.isQuestCompleted(q.number)).length;
        // Auto-expand if searching or if selected quest is in this act
        const hasSelectedQuest = selectedQuestNumber && quests.some(q => q.number === selectedQuestNumber);
        const isExpanded = hasSelectedQuest || (searchQuery && quests.length > 0);

        html += `
            <div class="quest-act ${isExpanded ? 'expanded' : ''}">
                <div class="act-header" onclick="window.questModal.toggleAct('${actKey}')">
                    <span class="act-name">${act.name}</span>
                    <span class="act-progress">${completedInAct}/${allQuests.length}</span>
                </div>
                <div class="act-quests" id="act-${actKey}">
        `;

        for (const questData of quests) {
            const questNum = questData.number;
            const quest = questData; // Already have full quest data

            const isCompleted = gameState.isQuestCompleted(questNum);
            const isActive = gameState.getActiveQuest()?.questNumber === questNum;
            const { canStart } = gameState.canStartQuest(questNum);
            const isSelected = selectedQuestNumber === questNum;
            const giver = QUEST_GIVERS[Object.keys(QUEST_GIVERS).find(k => QUEST_GIVERS[k].id === quest.giver)];

            let statusClass = 'locked';
            let statusIcon = '🔒';
            if (isCompleted) {
                statusClass = 'completed';
                statusIcon = '✓';
            } else if (isActive) {
                statusClass = 'active';
                statusIcon = '⏳';
            } else if (canStart) {
                statusClass = 'available';
                statusIcon = '!';
            }

            html += `
                <div class="quest-item ${statusClass} ${isSelected ? 'selected' : ''}"
                     onclick="window.questModal.selectQuest(${questNum})">
                    <span class="quest-status-icon">${statusIcon}</span>
                    <span class="quest-giver-icon">${giver?.icon || '?'}</span>
                    <span class="quest-item-name">${quest.name}</span>
                </div>
            `;
        }

        html += `
                </div>
            </div>
        `;
    }

    // Show no results message if searching
    if (searchQuery && !anyMatches) {
        html += `<div class="quest-no-results">No quests found matching "${searchQuery}"</div>`;
    }

    html += '</div>';
    return html;
}

/**
 * Render quest details panel
 */
function renderQuestDetails() {
    if (!selectedQuestNumber) {
        return `
            <div class="quest-details-empty">
                <p>Select a quest to view details</p>
            </div>
        `;
    }

    const quest = getQuest(selectedQuestNumber);
    if (!quest) return '';

    const giver = QUEST_GIVERS[Object.keys(QUEST_GIVERS).find(k => QUEST_GIVERS[k].id === quest.giver)];
    const isCompleted = gameState.isQuestCompleted(selectedQuestNumber);
    const isActive = gameState.getActiveQuest()?.questNumber === selectedQuestNumber;
    const { canStart, missing } = gameState.canStartQuest(selectedQuestNumber);

    let statusBadge = '';
    if (isCompleted) {
        statusBadge = '<span class="quest-status-badge completed">Completed</span>';
    } else if (isActive) {
        statusBadge = '<span class="quest-status-badge active">In Progress</span>';
    } else if (canStart) {
        statusBadge = '<span class="quest-status-badge available">Available</span>';
    } else {
        statusBadge = '<span class="quest-status-badge locked">Locked</span>';
    }

    // Build requirements HTML
    let requirementsHTML = renderRequirements(quest);

    // Build rewards HTML
    let rewardsHTML = renderRewards(quest);

    // Progress section for active quest
    let progressSection = '';
    if (isActive) {
        const progress = gameState.getQuestProgress();
        const isReady = progress >= 1;
        const elapsed = Date.now() - gameState.getActiveQuest().startedAt;
        const remaining = Math.max(0, quest.duration - elapsed);

        progressSection = `
            <div class="quest-detail-progress-section">
                <div class="quest-detail-timer ${isReady ? 'complete' : ''}">
                    ${isReady ? 'Ready to complete!' : `Time remaining: ${formatTime(remaining / 1000)}`}
                </div>
                <div class="quest-detail-progress-bar">
                    <div class="quest-detail-progress-fill" style="width: ${progress * 100}%"></div>
                </div>
            </div>
        `;
    }

    // Action buttons
    let actionsHTML = '';
    if (isCompleted) {
        actionsHTML = '<div class="quest-actions"><span class="quest-completed-text">Quest Completed</span></div>';
    } else if (isActive) {
        const isReady = gameState.isActiveQuestComplete();
        actionsHTML = `
            <div class="quest-actions">
                <button class="btn btn-primary"
                        onclick="window.questModal.completeQuest()"
                        ${!isReady ? 'disabled' : ''}>
                    ${isReady ? 'Claim Rewards' : 'In Progress...'}
                </button>
                ${!isReady ? `
                    <button class="btn btn-danger" onclick="window.questModal.cancelQuest()">
                        Abandon Quest
                    </button>
                ` : ''}
            </div>
        `;
    } else if (canStart) {
        const isPinned = gameState.isQuestPinned(selectedQuestNumber);
        actionsHTML = `
            <div class="quest-actions">
                <button class="btn btn-primary" onclick="window.questModal.startQuest(${selectedQuestNumber})">
                    Start Quest
                </button>
                <button class="btn btn-secondary" onclick="window.questModal.togglePin(${selectedQuestNumber})">
                    ${isPinned ? 'Unpin Quest' : 'Pin Quest'}
                </button>
            </div>
        `;
    } else {
        const isPinned = gameState.isQuestPinned(selectedQuestNumber);
        actionsHTML = `
            <div class="quest-actions quest-actions-blocked">
                <div class="quest-blocked-row">
                    <span class="blocked-label"><strong>Cannot start:</strong></span>
                    <button class="btn btn-secondary" onclick="window.questModal.togglePin(${selectedQuestNumber})">
                        ${isPinned ? 'Unpin Quest' : 'Pin Quest'}
                    </button>
                </div>
                <ul class="quest-missing-requirements-list">${missing.map(m => `<li>${m}</li>`).join('')}</ul>
            </div>
        `;
    }

    return `
        <div class="quest-detail-header">
            <span class="quest-detail-giver">${giver?.icon || '?'}</span>
            <div class="quest-detail-title">
                <h3>${quest.name}</h3>
                <span class="quest-detail-giverName">${giver?.name || 'Unknown'}</span>
            </div>
            ${statusBadge}
        </div>
        <div class="quest-detail-lore">
            <p>${quest.lore || 'No lore available.'}</p>
        </div>
        <div class="quest-detail-duration">
            <span class="duration-icon">⏱️</span>
            <span>Duration: ${formatTime(quest.duration / 1000)}</span>
        </div>
        ${progressSection}
        <div class="quest-detail-section">
            <h4>Requirements</h4>
            ${requirementsHTML || '<p class="no-requirements">No requirements</p>'}
        </div>
        <div class="quest-detail-section">
            <h4>Rewards</h4>
            ${rewardsHTML || '<p class="no-rewards">No rewards</p>'}
        </div>
        ${actionsHTML}
    `;
}

/**
 * Render quest requirements
 */
function renderRequirements(quest) {
    const req = quest.requirements;
    if (!req || Object.keys(req).length === 0) return '';

    let html = '<div class="quest-requirements">';

    // Skill requirements
    if (req.skills) {
        for (const [skillId, level] of Object.entries(req.skills)) {
            const skillData = skillRegistry.get(skillId);
            const playerLevel = gameState.getSkill(skillId)?.level || 0;
            const met = playerLevel >= level;
            html += `
                <div class="requirement-row ${met ? 'met' : 'unmet'}">
                    <span class="req-icon">${skillRegistry.getSkillIcon(skillId)}</span>
                    <span class="req-text">${skillData?.name || skillId} Level ${level}</span>
                    <span class="req-status">${met ? '✓' : `(${playerLevel}/${level})`}</span>
                </div>
            `;
        }
    }

    // Gold requirement
    if (req.gold) {
        const playerGold = gameState.getGold();
        const met = playerGold >= req.gold;
        html += `
            <div class="requirement-row ${met ? 'met' : 'unmet'}">
                <span class="req-icon">${getItemIcon('coins')}</span>
                <span class="req-text">${formatNumber(req.gold)} Gold</span>
                <span class="req-status">${met ? '✓' : `(${formatNumber(playerGold)}/${formatNumber(req.gold)})`}</span>
            </div>
        `;
    }

    // Item requirements (consumed on start)
    if (req.items) {
        for (const [itemId, qty] of Object.entries(req.items)) {
            const item = getItem(itemId);
            const playerQty = gameState.getItemCount(itemId);
            const bankQty = gameState.getBankItemCount(itemId);
            const met = playerQty >= qty;
            const canMeetWithBank = !met && (playerQty + bankQty) >= qty;

            let statusText = '✓';
            let statusClass = 'met';
            if (!met) {
                statusClass = canMeetWithBank ? 'can-meet-with-bank' : 'unmet';
                statusText = `(${playerQty}/${qty})`;
            }

            let bankIndicator = '';
            if (bankQty > 0 && !met) {
                bankIndicator = `<span class="bank-indicator">(+${bankQty} in bank)</span>`;
            }

            html += `
                <div class="requirement-row ${statusClass}">
                    <span class="req-icon">${getItemIcon(itemId)}</span>
                    <span class="req-text">${item?.name || itemId} x${qty}</span>
                    <span class="req-status">${statusText}</span>
                    ${bankIndicator}
                </div>
            `;
        }
        html += '<p class="requirement-note">Items consumed on quest start</p>';
    }

    // Quest requirements (clickable to navigate)
    if (req.quests) {
        for (const prereqNum of req.quests) {
            const prereq = getQuest(prereqNum);
            const met = gameState.isQuestCompleted(prereqNum);
            html += `
                <div class="requirement-row clickable ${met ? 'met' : 'unmet'}"
                     onclick="window.questModal.navigateToQuest(${prereqNum})"
                     data-tooltip="Click to view this quest">
                    <span class="req-icon">📜</span>
                    <span class="req-text req-link">Complete: ${prereq?.name || `Quest #${prereqNum}`}</span>
                    <span class="req-status">${met ? '✓' : '✗'}</span>
                </div>
            `;
        }
    }

    // Quest count requirement
    if (req.questCount) {
        const completed = gameState.getCompletedQuestCount();
        const met = completed >= req.questCount;
        html += `
            <div class="requirement-row ${met ? 'met' : 'unmet'}">
                <span class="req-icon">📚</span>
                <span class="req-text">Complete ${req.questCount} quests</span>
                <span class="req-status">${met ? '✓' : `(${completed}/${req.questCount})`}</span>
            </div>
        `;
    }

    // Milestone requirements
    if (req.milestones) {
        for (const milestoneId of req.milestones) {
            const met = gameState.isMilestoneCompleted(milestoneId);
            html += `
                <div class="requirement-row ${met ? 'met' : 'unmet'}">
                    <span class="req-icon">${getMilestoneIcon(milestoneId)}</span>
                    <span class="req-text">Build: ${getMilestoneDisplayName(milestoneId)}</span>
                    <span class="req-status">${met ? '✓' : '✗'}</span>
                </div>
            `;
        }
    }

    // Pet requirements
    if (req.pets) {
        for (const petId of Object.keys(req.pets)) {
            const pet = gameState.player.pets.find(p => p.id === petId);
            const met = pet?.unlocked;
            html += `
                <div class="requirement-row ${met ? 'met' : 'unmet'}">
                    <span class="req-icon">${getPetIcon(petId)}</span>
                    <span class="req-text">Unlock pet: ${getPetDisplayName(petId)}</span>
                    <span class="req-status">${met ? '✓' : '✗'}</span>
                </div>
            `;
        }
    }

    // Pet evolution requirements
    if (req.petEvolutions) {
        for (const [petId, level] of Object.entries(req.petEvolutions)) {
            const pet = gameState.player.pets.find(p => p.id === petId);
            const current = pet?.evolution || 0;
            const met = current >= level;
            html += `
                <div class="requirement-row ${met ? 'met' : 'unmet'}">
                    <span class="req-icon">${getPetIcon(petId)}</span>
                    <span class="req-text">Evolve ${getPetDisplayName(petId)} to level ${level}</span>
                    <span class="req-status">${met ? '✓' : `(${current}/${level})`}</span>
                </div>
            `;
        }
    }

    // Unlock requirements (must have obtained these from previous quests)
    if (req.unlocks) {
        for (const unlockId of req.unlocks) {
            const met = gameState.hasQuestUnlock(unlockId);
            const unlockName = getUnlockDisplayName(unlockId);
            html += `
                <div class="requirement-row ${met ? 'met' : 'unmet'}">
                    <span class="req-icon">🔓</span>
                    <span class="req-text">Obtained: ${unlockName}</span>
                    <span class="req-status">${met ? '✓' : '✗'}</span>
                </div>
            `;
        }
    }

    html += '</div>';
    return html;
}

/**
 * Build tooltip text for an item
 */
function buildItemTooltip(item) {
    if (!item) return '';

    let tooltip = item.name;

    // Add description if available
    if (item.description) {
        tooltip += `\n${item.description}`;
    }

    // For insights, show XP amount
    if (item.xpAmount) {
        tooltip += `\nGrants ${formatNumber(item.xpAmount)} XP`;
        if (item.minLevel > 1) {
            tooltip += `\nRequires skill level ${item.minLevel}`;
        }
    }

    return tooltip;
}

/**
 * Render quest rewards
 */
function renderRewards(quest) {
    const rewards = quest.rewards;
    if (!rewards) return '';

    let html = '<div class="quest-rewards">';

    // Item rewards
    if (rewards.items) {
        for (const [itemId, qty] of Object.entries(rewards.items)) {
            const item = getItem(itemId);
            const tooltip = buildItemTooltip(item);
            html += `
                <div class="reward-row" data-tooltip="${tooltip.replace(/"/g, '&quot;')}">
                    <span class="reward-icon">${getItemIcon(itemId)}</span>
                    <span class="reward-text">${item?.name || itemId} x${qty}</span>
                </div>
            `;
        }
    }

    // XP rewards (direct skill XP)
    if (rewards.xpRewards) {
        for (const [skillId, xpAmount] of Object.entries(rewards.xpRewards)) {
            const skillData = skillRegistry.get(skillId);
            const skillIconHtml = skillRegistry.getSkillIcon(skillId);
            const skillName = skillData?.name || skillId;
            html += `
                <div class="reward-row xp-reward" data-tooltip="${formatNumber(xpAmount)} XP awarded directly to ${skillName}">
                    <span class="reward-icon">${skillIconHtml}</span>
                    <span class="reward-text">+${formatNumber(xpAmount)} ${skillName} XP</span>
                </div>
            `;
        }
    }

    // Unlocks
    if (rewards.unlocks) {
        for (const unlockId of rewards.unlocks) {
            const unlockName = getUnlockDisplayName(unlockId);
            const unlockDesc = getUnlockDescription(unlockId);
            const isZoneUnlock = unlockId.startsWith('zone_');
            html += `
                <div class="reward-row unlock" data-tooltip="${unlockDesc.replace(/"/g, '&quot;')}">
                    <span class="reward-icon">🔓</span>
                    <span class="reward-text">${unlockName}</span>
                    ${isZoneUnlock ? `<button class="quest-view-zone-btn" onclick="window.questModal.navigateToZone()">View Zone</button>` : ''}
                </div>
            `;
        }
    }

    html += '</div>';
    return html;
}

/**
 * Get display name for an unlock
 */
function getUnlockDisplayName(unlockId) {
    const unlockNames = {
        'glyph_pouch': 'Glyph Pouch',
        'quiver_leather': 'Leather Quiver',
        'quiver_ironwood': 'Ironwood Quiver',
        'quiver_ashen': 'Ashen Quiver',
        'quiver_veilstalker': 'Veilstalker Quiver',
        'pet_eidolon': 'Eidolon Pet',
        'zone_ashen_crucible_t1': 'Ashen Crucible — Dungeon Bosses',
        'zone_ashen_crucible_t2': 'Ashen Crucible — Set Bosses',
        'zone_ashen_crucible_t3': 'Ashen Crucible — The Convergence',
        'contractor_hired': 'Contractor'
    };
    return unlockNames[unlockId] || unlockId;
}

/**
 * Get description for an unlock
 */
function getUnlockDescription(unlockId) {
    const unlockDescs = {
        'glyph_pouch': 'Allows equipping up to 6 glyphs for magic combat.\nGlyphs provide passive bonuses and special effects.',
        'quiver_leather': 'Ranged back slot equipment.\n+12 Accuracy, +3% Attack Speed, 5% Ammo Efficiency',
        'quiver_ironwood': 'Ranged back slot equipment.\n+22 Accuracy, +5% Attack Speed, 10% Ammo Efficiency',
        'quiver_ashen': 'Forged in pyre flames.\n+35 Accuracy, +8% Attack Speed, 15% Ammo Efficiency',
        'quiver_veilstalker': 'Touched by the Veil itself.\n+50 Accuracy, +12% Attack Speed, 20% Ammo Efficiency',
        'pet_eidolon': 'A mysterious spectral companion.\nThe rarest pet in the realm.',
        'zone_ashen_crucible_t1': 'The Ashen Crucible — Dungeon Bosses.\nFace the Ashen Warden, Pyre Guardian, and Flame Eternal.',
        'zone_ashen_crucible_t2': 'The Ashen Crucible — Set Bosses.\nChallenge the Searing Matriarch, Molten Colossus, and Cindered Knight.',
        'zone_ashen_crucible_t3': 'The Ashen Crucible — The Convergence.\nConfront the Convergence Herald, where all flames return.',
        'contractor_hired': 'Hire a real carpenter.\nYour contractor handles the actual building while you stick to what you\'re good at — which clearly isn\'t carpentry.'
    };
    return unlockDescs[unlockId] || getUnlockDisplayName(unlockId);
}

/**
 * Select a quest to view details
 */
export function selectQuest(questNumber) {
    selectedQuestNumber = questNumber;
    refreshModal();
}

/**
 * Toggle act expansion
 */
export function toggleAct(actKey) {
    const actEl = document.getElementById(`act-${actKey}`);
    if (actEl) {
        actEl.parentElement.classList.toggle('expanded');
    }
}

/**
 * Start a quest
 */
export async function startQuest(questNumber) {
    const quest = getQuest(questNumber);
    if (!quest) return;

    // Check if already has an active quest
    const currentActive = gameState.getActiveQuest();
    if (currentActive) {
        const currentQuest = getQuest(currentActive.questNumber);
        const confirmed = await showConfirmModal({
            title: 'Abandon Current Quest?',
            message: `You are currently on "${currentQuest?.name || 'Unknown Quest'}". Starting a new quest will abandon your current progress and any consumed items will NOT be refunded. Are you sure?`,
            confirmText: 'Yes, Start New Quest',
            cancelText: 'Keep Current Quest',
            confirmClass: 'danger'
        });

        if (!confirmed) return;

        // Cancel current quest
        gameState.cancelActiveQuest();
    }

    // Start the new quest
    const result = gameState.startQuest(questNumber);

    if (result.success) {
        toastSuccess(`Started quest: ${quest.name}`);
        logger.debug(`[QUEST] Started quest ${questNumber}: ${quest.name}`);
        refreshModal();

        // Update pinned quests panel (quest is auto-unpinned when started)
        if (window.pinnedQuestsUI?.render) {
            window.pinnedQuestsUI.render();
        }
    } else {
        toastWarning(result.reason);
    }
}

/**
 * Complete the active quest
 */
export function completeQuest() {
    const active = gameState.getActiveQuest();
    if (!active) {
        toastWarning('No active quest to complete');
        return;
    }

    const quest = getQuest(active.questNumber);

    const result = gameState.completeQuest();

    if (result.success) {
        toastSuccess(`Quest complete: ${quest?.name || 'Unknown'}!`);

        // Grant XP rewards
        if (result.rewards?.xpRewards && Object.keys(result.rewards.xpRewards).length > 0) {
            for (const [skillId, xpAmount] of Object.entries(result.rewards.xpRewards)) {
                addXP(skillId, xpAmount, null, { skipCorruption: true });
                const skillData = skillRegistry.get(skillId);
                const skillName = skillData?.name || skillId;
                toastSuccess(`+${formatNumber(xpAmount)} ${skillName} XP!`);
            }
        }

        // Log unlocks
        if (result.rewards?.unlocks?.length > 0) {
            for (const unlock of result.rewards.unlocks) {
                toastSuccess(`Unlocked: ${getUnlockDisplayName(unlock)}`);
            }
        }

        logger.debug(`[QUEST] Completed quest ${active.questNumber}`, result.rewards);
        refreshModal();
    } else {
        toastError(result.reason);
    }
}

/**
 * Cancel/abandon the active quest
 */
export async function cancelQuest() {
    const active = gameState.getActiveQuest();
    if (!active) return;

    const quest = getQuest(active.questNumber);

    const confirmed = await showConfirmModal({
        title: 'Abandon Quest?',
        message: `Are you sure you want to abandon "${quest?.name || 'Unknown Quest'}"? Any consumed items or gold will NOT be refunded.`,
        confirmText: 'Abandon Quest',
        cancelText: 'Keep Quest',
        confirmClass: 'danger'
    });

    if (!confirmed) return;

    gameState.cancelActiveQuest();
    toastWarning(`Abandoned quest: ${quest?.name || 'Unknown'}`);
    logger.debug(`[QUEST] Abandoned quest ${active.questNumber}`);
    refreshModal();
}

/**
 * Toggle pin status for a quest
 */
export function togglePin(questNumber) {
    const isPinned = gameState.isQuestPinned(questNumber);

    if (isPinned) {
        gameState.unpinQuest(questNumber);
        toastSuccess('Quest unpinned');
    } else {
        const result = gameState.pinQuest(questNumber);
        if (result.success) {
            toastSuccess('Quest pinned for tracking');
        } else {
            toastWarning(result.reason);
        }
    }

    refreshModal();

    // Update pinned quests UI if available
    if (window.pinnedQuestsUI?.render) {
        window.pinnedQuestsUI.render();
    }
}

/**
 * Navigate to a specific quest (used for prerequisite quest links)
 * Opens the quest modal if not already open
 */
export function navigateToQuest(questNumber) {
    selectedQuestNumber = questNumber;
    searchQuery = ''; // Clear search when navigating

    // Check if modal is already open
    const existingModal = document.querySelector('.quest-modal-overlay');
    if (existingModal) {
        refreshModal();
    } else {
        // Open the modal with this quest selected
        renderModal();
    }

    // Scroll the quest into view in the sidebar
    requestAnimationFrame(() => {
        const questItem = document.querySelector(`.quest-item[onclick*="selectQuest(${questNumber})"]`);
        if (questItem) {
            questItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    });
}

/**
 * Navigate from quest modal to the Combat skill (zones tab)
 * Used for zone-unlock quest rewards
 */
function navigateToZone() {
    closeQuestModal();
    window.location.hash = '#/combat';
}

/**
 * Refresh the modal content
 */
function refreshModal() {
    const body = document.querySelector('.quest-modal-body');
    if (!body) return;

    // Update count in header
    const countEl = document.querySelector('.quest-count');
    if (countEl) {
        const completed = gameState.getCompletedQuestCount();
        const total = getTotalQuestCount();
        countEl.textContent = `${completed}/${total}`;
    }

    // Re-render sidebar and details
    body.innerHTML = `
        <div class="quest-sidebar">
            ${renderSearchBar()}
            ${renderActiveQuestTracker()}
            ${renderQuestList()}
        </div>
        <div class="quest-details">
            ${renderQuestDetails()}
        </div>
    `;
}

/**
 * Get the quest button display text for the header
 */
export function getQuestButtonText() {
    const completed = gameState.getCompletedQuestCount();
    const total = getTotalQuestCount();
    return `${completed}/${total}`;
}

/**
 * Check if there's an active quest ready to complete
 */
export function hasQuestReadyToComplete() {
    return gameState.hasActiveQuest() && gameState.isActiveQuestComplete();
}

/**
 * Get the active quest timer text for header display
 * @returns {string} Timer text like "⏳ 45m 30s" or "✓ Complete!" or empty string
 */
export function getActiveQuestTimerText() {
    const active = gameState.getActiveQuest();
    if (!active) return '';

    const quest = getQuest(active.questNumber);
    if (!quest) return '';

    const progress = gameState.getQuestProgress();
    if (progress >= 1) {
        return '✓ Ready!';
    }

    const elapsed = Date.now() - active.startedAt;
    const remaining = Math.max(0, quest.duration - elapsed);
    return `⏳ ${formatTime(remaining / 1000)}`;
}

// Expose to window for onclick handlers
window.questModal = {
    close: closeQuestModal,
    selectQuest,
    toggleAct,
    startQuest,
    completeQuest,
    cancelQuest,
    handleSearch,
    clearSearch,
    navigateToQuest,
    navigateToZone,
    togglePin
};

export default {
    showQuestModal,
    closeQuestModal,
    getQuestButtonText,
    hasQuestReadyToComplete
};
